import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { subscriptionsService } from '@/services/subscriptions';
import { useAuthStore } from '@/stores/authStore';
import type { SubscriptionStatus } from '@dpfs/shared';
import { AlertTriangle, XCircle, Clock } from 'lucide-react';

const Status: Record<string, SubscriptionStatus> = {
  ACTIVE: 'ACTIVE' as SubscriptionStatus,
  PAST_DUE: 'PAST_DUE' as SubscriptionStatus,
  EXPIRED: 'EXPIRED' as SubscriptionStatus,
  CANCELLED: 'CANCELLED' as SubscriptionStatus,
};

export function SubscriptionBanner() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: () => subscriptionsService.getCurrentSubscription(),
    enabled: user?.role === 'PHARMACIST',
    staleTime: 5 * 60 * 1000,
  });

  if (user?.role !== 'PHARMACIST') return null;

  // Don't flash banner while loading
  if (isLoading) return null;

  const subscription = data?.subscription;
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';

  // No subscription
  if (!subscription) {
    return (
      <Banner variant="red" icon={XCircle}>
        <span>{t('subscription.noSubscription')}</span>
        <Link to="/pharmacist/subscription" className="font-semibold underline underline-offset-2 hover:no-underline">
          {t('subscription.subscribe')}
        </Link>
      </Banner>
    );
  }

  // Expired
  if (subscription.status === Status.EXPIRED) {
    return (
      <Banner variant="red" icon={XCircle}>
        <span>{t('subscription.expired')}</span>
        <Link to="/pharmacist/subscription" className="font-semibold underline underline-offset-2 hover:no-underline">
          {t('subscription.renew')}
        </Link>
      </Banner>
    );
  }

  // Past due (grace period)
  if (subscription.status === Status.PAST_DUE) {
    return (
      <Banner variant="amber" icon={AlertTriangle}>
        <span>{t('subscription.gracePeriod', { days: Math.max(0, subscription.daysUntilExpiry) })}</span>
        <Link to="/pharmacist/subscription" className="font-semibold underline underline-offset-2 hover:no-underline">
          {t('subscription.renew')}
        </Link>
      </Banner>
    );
  }

  // Cancelled
  if (subscription.status === Status.CANCELLED) {
    return (
      <Banner variant="amber" icon={AlertTriangle}>
        <span>
          {t('subscription.cancelledUntil', {
            date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale),
          })}
        </span>
      </Banner>
    );
  }

  // Active, expiring soon (<=7 days)
  if (subscription.status === Status.ACTIVE && subscription.daysUntilExpiry <= 7) {
    return (
      <Banner variant="blue" icon={Clock}>
        <span>{t('subscription.expiresIn', { days: subscription.daysUntilExpiry })}</span>
      </Banner>
    );
  }

  // Active, no banner
  return null;
}

function Banner({
  variant,
  icon: Icon,
  children,
}: {
  variant: 'red' | 'amber' | 'blue';
  icon: typeof AlertTriangle;
  children: React.ReactNode;
}) {
  const styles = {
    red: 'alert-danger',
    amber: 'alert-warning',
    blue: 'alert-info',
  };

  return (
    <div role="status" className={`${styles[variant]} mb-6 items-center px-4 py-3`}>
      <Icon className="h-4 w-4 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">{children}</div>
    </div>
  );
}
